// =============================================================================
// CallVerdict — one audit verdict per call, written by the Call Judge
// =============================================================================
// Deliberately a separate collection rather than fields on CallSession: a verdict can be
// recomputed, re-run with a changed rulebook, or deleted wholesale without touching the cost
// analytics that the rest of the Test Lab depends on.
//
// PII: `findingsJSON` contains VERBATIM quotes from the call — including whatever the caller said.
// It therefore inherits the same retention rules as the transcripts themselves. Nothing here is
// sent to the CRM, and the verdict is never a gate on anything: it is triage, not a pass mark.
//
// callID is the key. The UI builds the link from it — a stored URL would point at the wrong
// hostname the moment a staging verdict is read from production.
// =============================================================================

import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

@Entity()
export class CallVerdict extends MongoEntity {
  static override _class = 'CallVerdict';
  static override _collectionName = 'call_verdicts';

  constructor(data: PartialEntityData<CallVerdict>) {
    super(data);
  }

  /** The call this verdict belongs to. The UI turns it into /calls/<callID>. */
  @Field(() => String) callID: Maybe<string>;
  @Field(() => Date) judgedAt: Maybe<Date>;

  /**
   * When the CALL happened — not when it was judged. Taken from the call log itself rather than
   * from `call_sessions`, because on this box 35 calls have logs and only 26 have a session row.
   * The log is the one record that is always there.
   */
  @Field(() => Date) callStartedAt: Maybe<Date>;
  @Field(() => Date) callEndedAt: Maybe<Date>;
  @Field(() => Int) callDurationMs: Maybe<number>;

  /**
   * Feature flags at judge time, as JSON. Data for a human reading an old verdict — the judge
   * itself is never shown this, because a flag's effect is already in the transcript.
   */
  @Field(() => String) flagsJSON: Maybe<string>;

  /** Path derived from the tool calls, in code. */
  @Field(() => String) path: Maybe<string>;
  /** Path the judge thought the conversation looked like. A mismatch is itself a finding. */
  @Field(() => String) pathJudged: Maybe<string>;

  /** none | minor | major | critical — the worst finding in this call. */
  @Field(() => String) severity: Maybe<string>;
  @Field(() => Int) findingCount: Maybe<number>;
  /** JSON array of findings: rule, severity, turn, clock, instruction, response, reason, source. */
  @Field(() => String) findingsJSON: Maybe<string>;

  @Field(() => Int) turnCount: Maybe<number>;
  @Field(() => String) model: Maybe<string>;
  @Field(() => Int) elapsedMs: Maybe<number>;
  /** Set when the judge itself failed. Mechanical findings still stand in that case. */
  @Field(() => String) error: Maybe<string>;
}
